import { useSyncExternalStore } from 'react';
import { subscribeLoading, getIsLoading } from '@/lib/globalLoading';
import '@/styles/neon-loading-line.css';

const GRADIENT_DEFAULT = '#ff6b6b, #00e5ff, #00bfa5, #ff6b6b';
const GRADIENT_ADMIN = '#80d8ff, #00e5ff, #1a3a8a, #80d8ff';

export function NeonLoadingLine() {
  const isLoading = useSyncExternalStore(subscribeLoading, getIsLoading);

  return (
    <div
      style={{
        position: 'relative',
        zIndex: 20,
        height: isLoading ? 3 : 2,
        opacity: isLoading ? 1 : 0.3,
        background: `linear-gradient(90deg, ${GRADIENT_DEFAULT}, #00e5ff, #00bfa5, ${GRADIENT_DEFAULT})`,
        backgroundSize: '300% 100%',
        animation: isLoading
          ? 'neonFlow 4s linear infinite, neonPulse 6s ease-in-out infinite'
          : 'neonFlow 14s linear infinite',
        pointerEvents: 'none',
        transition: isLoading
          ? 'height 0.3s ease, opacity 0.3s ease, filter 0.3s ease'
          : 'height 1.2s ease-out, opacity 1.2s ease-out, filter 1.2s ease-out',
      }}
    />
  );
}

/**
 * Circulating neon border — flows around the entire perimeter of the parent.
 * Parent must have `position: relative` (or fixed/absolute) so this overlay aligns.
 */
export function NeonBorder({ variant = 'default' }: { variant?: 'default' | 'admin' }) {
  const isLoading = useSyncExternalStore(subscribeLoading, getIsLoading);
  const isAdmin = variant === 'admin';
  const gradient = isAdmin ? GRADIENT_ADMIN : GRADIENT_DEFAULT;
  const pulse = isAdmin ? 'neonPulseAdmin' : 'neonPulse';

  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        borderRadius: 'inherit',
        padding: isLoading ? 1.5 : 1,
        background: `conic-gradient(from var(--neon-angle), ${gradient})`,
        WebkitMask:
          'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
        WebkitMaskComposite: 'xor',
        mask:
          'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
        maskComposite: 'exclude',
        animation: isLoading
          ? `neonRotate 3s linear infinite, ${pulse} 6s ease-in-out infinite`
          : 'neonRotateSlow 12s linear infinite',
        opacity: isLoading ? 1 : 0.4,
        pointerEvents: 'none',
        zIndex: 1,
        transition: isLoading
          ? 'padding 0.3s ease, opacity 0.3s ease, filter 0.3s ease'
          : 'padding 1.2s ease-out, opacity 1.2s ease-out, filter 1.2s ease-out',
      }}
    />
  );
}
